import { useCallback, useRef, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { DonorSchedule } from '@/rtk-query.gql';
import { SiteOption } from '@/services/sites';

import { EditDonorSchedule, Grid, Toolbar } from './components';
import { WorkflowHistoryDialog } from './components/workflow-history-dialog';
import {
  useContextMenuActions,
  ValidationIssue,
} from './hooks/useContextMenuActions';

export type SiteFilterHandle = {
  getSites: () => SiteOption[];
  setSites: (sites: SiteOption[]) => void;
};

export const DonorSchedulingPartnershipPage = () => {
  const [params] = useSearchParams();
  const isEditVisible = params.get('edit');
  const gridRef = useRef<AgGridReact>(null);
  const siteFilterRef = useRef<SiteFilterHandle>({
    getSites: () => [],
    setSites: () => {},
  });

  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const [soScheduleWarningDialogVisible, setSoScheduleWarningDialogVisible] =
    useState(false);
  const [soScheduleValidationIssues, setSoScheduleValidationIssues] = useState<
    ValidationIssue[]
  >([]);

  const [workflowHistoryOpen, setWorkflowHistoryOpen] = useState(false);
  const [workflowHistoryEntity, setWorkflowHistoryEntity] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const contextMenuActions = useContextMenuActions();

  const handleSoScheduleWarning = useCallback(
    (validationIssues: ValidationIssue[]) => {
      setSoScheduleValidationIssues(validationIssues);
      setSoScheduleWarningDialogVisible(true);
    },
    [],
  );

  const handleOpenWorkflowHistory = useCallback(() => {
    const single =
      selectedDonorSchedules?.length === 1
        ? (selectedDonorSchedules[0] as DonorSchedule)
        : null;

    if (!single?.id) return;

    setWorkflowHistoryEntity({ id: single.id, name: single.name ?? '' });
    setWorkflowHistoryOpen(true);
  }, [selectedDonorSchedules]);

  const handleSoScheduleWarningAccept = useCallback(async () => {
    setSoScheduleWarningDialogVisible(false);
    await contextMenuActions.handleSoScheduleWarningAccept();
  }, [contextMenuActions]);

  return (
    <div className="donor-scheduling-partnership-page common-page">
      <Toolbar
        onSoScheduleWarning={handleSoScheduleWarning}
        contextMenuActions={contextMenuActions}
        gridRef={gridRef}
        onOpenWorkflowHistory={handleOpenWorkflowHistory}
        siteFilterRef={siteFilterRef}
      />
      <Grid
        soScheduleWarningDialogVisible={soScheduleWarningDialogVisible}
        setSoScheduleWarningDialogVisible={setSoScheduleWarningDialogVisible}
        soScheduleValidationIssues={soScheduleValidationIssues}
        contextMenuActions={contextMenuActions}
        onSoScheduleWarningAccept={handleSoScheduleWarningAccept}
        gridRef={gridRef}
        onOpenWorkflowHistory={handleOpenWorkflowHistory}
        siteFilterRef={siteFilterRef}
      />
      {isEditVisible && <EditDonorSchedule />}
      <WorkflowHistoryDialog
        open={workflowHistoryOpen}
        onOpenChange={setWorkflowHistoryOpen}
        entityId={workflowHistoryEntity?.id ?? null}
        entityName={workflowHistoryEntity?.name}
      />
    </div>
  );
};
